import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCountryLabel } from '../utils/countryConfig';
import { getProductVariants } from '../utils/productVariants';
import {
  commitSampleAllocation, createProductSamples, listSampleProducts, matchSampleProducts,
  MAX_SAMPLE_BATCH, previewSampleAllocation, sampleErrorMessage, subscribeProductSamples,
} from '../utils/productSamples';
import { SampleEditorRow, sampleStyles, useSampleAuthorization } from './ProductSamples';
import ZxingBarcodeScanner from './ZxingBarcodeScanner';

export default function SampleBackfill({ country, onClose }) {
  const authorization = useSampleAuthorization();
  const [samples, setSamples] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [barcode, setBarcode] = useState('');
  const [matches, setMatches] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [newCount, setNewCount] = useState('1');
  const [newLocation, setNewLocation] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [dirtyIds, setDirtyIds] = useState([]);
  const [prefix, setPrefix] = useState('');
  const [start, setStart] = useState('1');
  const [width, setWidth] = useState('4');
  const [useCounter, setUseCounter] = useState(true);
  const [replaceLocation, setReplaceLocation] = useState(false);
  const [batchLocation, setBatchLocation] = useState('');
  const [preview, setPreview] = useState(null);
  const [visibleCount, setVisibleCount] = useState(100);
  const numberInputs = useRef({});
  const barcodeInput = useRef(null);
  const searchRequest = useRef(0);

  useEffect(() => {
    let active = true;
    searchRequest.current += 1;
    setSamples([]);
    setProducts([]);
    setMatches(null);
    setSelectedProductId('');
    setVariantId('');
    setSelectedIds([]);
    setDirtyIds([]);
    setPreview(null);
    setLoading(true);
    setBusy(false);
    setScanning(false);
    setError('');
    if (!authorization.authorized) return undefined;
    listSampleProducts(country).then((next) => { if (active) setProducts(next); })
      .catch((failure) => { if (active) setError(sampleErrorMessage(failure)); });
    const unsubscribe = subscribeProductSamples(country, null, (next) => {
      if (active) { setSamples(next); setLoading(false); }
    }, (failure) => { if (active) { setError(sampleErrorMessage(failure)); setLoading(false); } });
    return () => { active = false; searchRequest.current += 1; unsubscribe(); };
  }, [country, authorization.authorized]);

  const productById = new Map(products.map((product) => [product.id, product]));
  const selectedMatch = matches?.find((match) => match.productId === selectedProductId);
  // Keep a dirty row visible if another editor numbers it during this session.
  const unnumbered = samples.filter((sample) => sample.number === '' || dirtyIds.includes(sample.id));
  const filtered = unnumbered.filter((sample) => (!selectedProductId || sample.productId === selectedProductId)
    && (!variantId || !sample.variantId || sample.variantId === variantId));
  const visible = filtered.slice(0, visibleCount);
  const sampleLabel = (sample) => {
    const product = productById.get(sample.productId);
    const variant = getProductVariants(product || {}).find((entry) => entry.id === sample.variantId);
    return `${product?.name || sample.productId} · ${sample.variantId ? variant?.name || sample.variantId : '商品本體／未指定款式'} · ${sample.id.slice(-6)}`;
  };
  const clearSelection = () => { setSelectedIds([]); setPreview(null); };
  const onDirtyChange = useCallback((id, dirty) => {
    setDirtyIds((current) => dirty
      ? current.includes(id) ? current : [...current, id]
      : current.includes(id) ? current.filter((value) => value !== id) : current);
    if (dirty) { setSelectedIds((current) => current.filter((value) => value !== id)); setPreview(null); }
  }, []);
  const findBarcode = async (value) => {
    if (busy || !value) return;
    const request = ++searchRequest.current;
    setBusy(true);
    setScanning(false);
    setError('');
    setMessage('');
    setSelectedProductId('');
    setVariantId('');
    clearSelection();
    try {
      // Reload the product catalog so a recent barcode edit is not hidden by a stale scan cache.
      const next = await listSampleProducts(country);
      if (request !== searchRequest.current) return;
      setProducts(next);
      setMatches(matchSampleProducts(next, value));
    } catch (failure) { if (request === searchRequest.current) setError(sampleErrorMessage(failure)); }
    finally { if (request === searchRequest.current) setBusy(false); }
  };
  const chooseMatch = (match) => {
    setSelectedProductId(match.productId);
    setVariantId(match.variants.length === 1 && match.variants[0].active !== false ? match.variants[0].id : '');
    clearSelection();
    setVisibleCount(100);
  };
  const addPieces = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (!/^\d+$/.test(newCount)) throw new Error('件數須為整數');
      const ids = await createProductSamples({ country, productId: selectedProductId, variantId, count: Number(newCount), location: newLocation });
      setMessage(`已新增 ${ids.length} 件未編號樣品，可在下方逐件輸入或批次編號`);
    } catch (failure) { setError(sampleErrorMessage(failure)); }
    finally { setBusy(false); }
  };
  const selectSample = (id) => {
    setPreview(null);
    if (!selectedIds.includes(id) && selectedIds.length >= MAX_SAMPLE_BATCH) {
      setError(`每次最多選取 ${MAX_SAMPLE_BATCH} 件；請完成這批後再繼續`);
      return;
    }
    setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  };
  const nextInput = (id) => {
    const index = filtered.findIndex((sample) => sample.id === id);
    const next = filtered.slice(index + 1).find((sample) => numberInputs.current[sample.id]);
    if (next) numberInputs.current[next.id].focus();
    else barcodeInput.current?.focus();
  };
  const makePreview = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    setPreview(null);
    try {
      if ((!useCounter && !/^\d+$/.test(start)) || !/^\d+$/.test(width)) throw new Error('起號及補零寬度須為整數');
      const selected = selectedIds.map((id) => samples.find((sample) => sample.id === id));
      if (selected.some((sample) => !sample)) throw new Error('有樣品已變更，請重新選取');
      const next = await previewSampleAllocation({ samples: selected, prefix, start: Number(start), width: Number(width), useCounter,
        ...(replaceLocation ? { location: batchLocation } : {}) });
      setPreview(next);
    } catch (failure) { setError(sampleErrorMessage(failure)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (busy || !preview) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await commitSampleAllocation(preview);
      setMessage(`已依預覽完成 ${result.length} 件樣品編號：${result[0].number} ～ ${result[result.length - 1].number}`);
      clearSelection();
      barcodeInput.current?.focus();
    } catch (failure) { setError(sampleErrorMessage(failure)); }
    finally { setBusy(false); }
  };
  const changeOption = (setter) => (event) => { setter(event.target.value); setPreview(null); };

  return (
    <section style={{ ...sampleStyles.card, maxWidth: 1050, margin: '20px auto' }} aria-label="樣品快速補編">
      <div style={{ ...sampleStyles.row, justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>{getCountryLabel(country)} · 樣品快速補編</h2>
        {onClose && <button type="button" onClick={onClose} disabled={busy} style={sampleStyles.button}>關閉</button>}
      </div>
      <p style={sampleStyles.muted}>掃描商品條碼 → 確認商品與款式 → 逐件輸入編號及位置。Enter 儲存並跳下一件；同款多件請分別登記。編號跨國家唯一，前導零不會消失</p>
      {authorization.loading ? <p role="status">確認登入身份…</p> : !authorization.authorized ? <p role="alert">請先使用管理員或工作人員帳號登入</p> : <>
        <div style={sampleStyles.row}>
          <label style={sampleStyles.label}>精確商品條碼（原始或完整條碼）
            <input ref={barcodeInput} type="text" value={barcode} autoComplete="off" style={sampleStyles.input} disabled={busy}
              onChange={(event) => setBarcode(event.target.value)} onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); findBarcode(barcode); }
              }} />
          </label>
          <button type="button" onClick={() => findBarcode(barcode)} disabled={busy || !barcode} style={sampleStyles.primary}>查找全部符合商品</button>
          <button type="button" onClick={() => setScanning((current) => !current)} disabled={busy} style={sampleStyles.button}>{scanning ? '停止掃描' : '開啟相機掃描'}</button>
          <button type="button" onClick={() => { setMatches(null); setSelectedProductId(''); setVariantId(''); clearSelection(); }} disabled={busy} style={sampleStyles.button}>全部未編號樣品</button>
        </div>
        <label style={{ ...sampleStyles.label, marginTop: 12 }}>或直接選擇商品（含待補條碼商品）
          <select value={selectedProductId} disabled={busy} style={sampleStyles.input} onChange={(event) => {
            const product = productById.get(event.target.value);
            if (!product) { setSelectedProductId(''); setVariantId(''); clearSelection(); return; }
            const match = { productId: product.id, product, variants: getProductVariants(product).filter((entry) => entry.active !== false) };
            setMatches([match]);
            chooseMatch(match);
          }}>
            <option value="">請選擇商品</option>
            {products.map((product) => <option key={product.id} value={product.id}>{product.name || '未命名商品'} · {product.itemNumber || '無料號'} · {product.id.slice(-6)}</option>)}
          </select>
        </label>
        {scanning && <ZxingBarcodeScanner scanning={scanning} onScan={(value) => { setBarcode(value); findBarcode(value); }} />}
        {matches !== null && <div style={{ background: '#f1f5f9', padding: 12, borderRadius: 8, marginTop: 12 }}>
          <strong>{matches.length ? `找到 ${matches.length} 個商品，請明確選擇：` : '找不到完全符合的條碼，請檢查原始條碼或完整條碼'}</strong>
          {matches.map((match) => <div key={match.productId} style={{ marginTop: 8 }}>
            <button type="button" onClick={() => chooseMatch(match)} disabled={busy} aria-pressed={selectedProductId === match.productId}
              style={selectedProductId === match.productId ? sampleStyles.primary : sampleStyles.button}>
              {match.product.name || '未命名商品'} · 料號 {match.product.itemNumber || '無'} · {match.variants.map((variant) => variant.name || '無款式').join('／')} · {match.productId.slice(-6)}
            </button>
          </div>)}
        </div>}
        {selectedMatch && <div style={{ ...sampleStyles.card, background: '#f8fafc' }}>
          <h3 style={{ marginTop: 0 }}>商品：{selectedMatch.product.name}</h3>
          <div style={sampleStyles.row}>
            <label style={sampleStyles.label}>確認款式
              <select style={sampleStyles.input} value={variantId} disabled={busy} onChange={(event) => { setVariantId(event.target.value); clearSelection(); }}>
                <option value="">商品本體／未指定款式</option>
                {selectedMatch.variants.filter((variant) => variant.active !== false).map((variant) => <option key={variant.id} value={variant.id}>{variant.name || '無款式'} · {variant.rawBarcode || variant.barcode}</option>)}
              </select>
            </label>
            <label style={{ ...sampleStyles.label, flex: '0 1 95px' }}>新增件數
              <input type="number" min="1" max={MAX_SAMPLE_BATCH} step="1" value={newCount} onChange={(event) => setNewCount(event.target.value)} disabled={busy} style={sampleStyles.input} />
            </label>
            <label style={sampleStyles.label}>新增樣品位置
              <input value={newLocation} maxLength={200} onChange={(event) => setNewLocation(event.target.value)} disabled={busy} style={sampleStyles.input} />
            </label>
            <button type="button" disabled={busy || !selectedProductId} onClick={addPieces} style={sampleStyles.primary}>登記未編號實體樣品</button>
          </div>
        </div>}

        <section style={{ ...sampleStyles.card, background: '#f8fafc' }} aria-label="批次編號">
          <h3 style={{ marginTop: 0 }}>批次編號 · 已選 {selectedIds.length} 件</h3>
          <p style={sampleStyles.muted}>只分配給下方勾選的未編號樣品。預覽後整批一次儲存；任何衝突都不會部分套用</p>
          <div style={sampleStyles.row}>
            <label style={sampleStyles.label}>前綴（可空白）<input value={prefix} onChange={changeOption(setPrefix)} disabled={busy} maxLength={80} style={sampleStyles.input} /></label>
            <label style={sampleStyles.label}>手動起號<input type="number" min="0" step="1" value={start} onChange={changeOption(setStart)} disabled={busy || useCounter} style={sampleStyles.input} /></label>
            <label style={sampleStyles.label}>補零寬度<input type="number" min="1" max="12" value={width} onChange={changeOption(setWidth)} disabled={busy} style={sampleStyles.input} /></label>
          </div>
          <p><label><input type="checkbox" checked={useCounter} disabled={busy} onChange={(event) => { setUseCounter(event.target.checked); setPreview(null); }} /> 使用跨國家共用的下一個起號（預覽後若被占用，須重新預覽）</label></p>
          <div style={sampleStyles.row}>
            <label><input type="checkbox" checked={replaceLocation} disabled={busy} onChange={(event) => { setReplaceLocation(event.target.checked); setPreview(null); }} /> 同時設定這批位置</label>
            <input aria-label="批次位置" value={batchLocation} onChange={changeOption(setBatchLocation)} disabled={busy || !replaceLocation} maxLength={200} style={{ ...sampleStyles.input, width: 240 }} />
            <button type="button" disabled={busy || !selectedIds.length} onClick={makePreview} style={sampleStyles.button}>{busy ? '處理中…' : '產生精確預覽'}</button>
          </div>
          {preview && <div style={{ marginTop: 12 }}>
            <strong>將套用以下 {preview.assignments.length} 件（起號 {preview.start}）</strong>
            <ol>{preview.assignments.map((entry) => <li key={entry.sampleId} style={{ padding: '6px 0', overflowWrap: 'anywhere' }}>
              {sampleLabel({ ...entry, id: entry.sampleId })} → 編號「{entry.number}」 · 位置「{entry.location || '未設定'}」
            </li>)}</ol>
            <button type="button" disabled={busy} onClick={commit} style={sampleStyles.primary}>{busy ? '儲存中…' : '確認，整批套用此預覽'}</button>
          </div>}
        </section>
        {error && <p role="alert" style={sampleStyles.error}>{error}</p>}
        {message && <p role="status">{message}</p>}
        <div style={{ ...sampleStyles.row, marginTop: 18, justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>未編號樣品 {filtered.length} 件{selectedProductId ? '（目前選取商品／款式）' : '（此國家全部）'}</h3>
          <button type="button" disabled={busy || !filtered.length} style={sampleStyles.button}
            onClick={() => { setSelectedIds(filtered.filter((sample) => sample.number === '' && !dirtyIds.includes(sample.id)).slice(0, MAX_SAMPLE_BATCH).map((sample) => sample.id)); setPreview(null); }}>選取前 {Math.min(filtered.filter((sample) => sample.number === '' && !dirtyIds.includes(sample.id)).length, MAX_SAMPLE_BATCH)} 件</button>
          <button type="button" disabled={busy || !selectedIds.length} style={sampleStyles.button} onClick={clearSelection}>取消勾選</button>
        </div>
        {loading ? <p role="status">讀取樣品…</p> : !filtered.length ? <p style={sampleStyles.muted}>沒有未編號樣品；可掃碼找到商品後登記實體樣品</p> : visible.map((sample) => (
          <SampleEditorRow key={sample.id} sample={sample} label={sampleLabel(sample)} selected={selectedIds.includes(sample.id)} onSelect={selectSample}
            onSaved={nextInput} onDirtyChange={onDirtyChange} disabled={busy} inputRef={(element) => { if (element) numberInputs.current[sample.id] = element; else delete numberInputs.current[sample.id]; }} />
        ))}
        {filtered.length > visibleCount && <button type="button" style={sampleStyles.button} onClick={() => setVisibleCount((value) => value + 100)}>再顯示 100 件（尚有 {filtered.length - visibleCount} 件）</button>}
      </>}
    </section>
  );
}
